import { z } from 'zod';
import { subscriptionGrantModeSchema } from '@hero/schema';
import type { Currency, SubscriptionGrantMode, TaxId } from '@hero/schema';
import {
  DEFAULT_SUBSCRIPTION_AUDIENCE,
  SUBSCRIPTION_CONFIG,
  defaultUnlimitedPlanCode,
  freeCreditsPolicy,
  planHasUnlimitedOrders,
  salesConfig,
  truckCountIssue,
  usageOrderTiers,
  usagePriceDisplayMode,
  usageTaxConfig,
  type FreeCreditsPolicy,
  type FreeWindowStart,
  type PerOrderPlanConfig,
  type PricingFlow,
  type PricingOffer,
  type SalesConfig,
  type SeatPlansConfig,
  type SubscriptionAudience,
  type UnlimitedPlanConfig,
  type UsagePriceDisplayMode,
  type UsageTierConfig,
  getPlansForBusiness,
  isUsagePlan,
} from '../config/subscription.config';
import type { Business } from '../config/app-registry.config';

/** An unlimited plan after normalization — always names the seat plan it bills on. */
export type ResolvedUnlimitedPlan = UnlimitedPlanConfig & { planCode: string };

/** A sellable plan. The per-order ladder itself is on {@link ResolvedUsagePricing}. */
export type ResolvedSalesPlan =
  | { id: string; kind: 'per_order' }
  | ResolvedUnlimitedPlan
  | SeatPlansConfig;

/** A `menu` option whose plan exists and can sell its quantity. */
export type ResolvedMenuOption =
  | { id: string; kind: 'per_order'; plan: string; qty: number; highlight?: boolean }
  | { id: string; kind: 'unlimited'; plan: string; vehicleCount: number; highlight?: boolean };

/**
 * What one audience is sold and how, after the PostHog overlay and
 * normalization: `plans` only holds plans the business can actually
 * sell, `menu` only options of those plans, `offers` is the kinds of
 * `plans` in presentation order, and a `menu` flow with nothing left
 * in it falls back to `choose_model`.
 */
export type ResolvedSalesConfig = {
  flow: PricingFlow;
  offers: PricingOffer[];
  plans: ResolvedSalesPlan[];
  menu: ResolvedMenuOption[];
};

/**
 * What one audience on one brand is quoted and charged.
 *
 * `tiers` is the per-order ladder they see. `taxInclusive` is whether
 * each `pricePerUnit` already includes VAT. `taxId` is which tax
 * {@link amountForUsagePurchase} applies (`null` = none).
 * `displayMode` is how the card leads each rung — not how it is charged.
 * `sales` is which plans are sold, in which flow, at which prices.
 */
export type ResolvedUsagePricing = {
  variant?: string;
  taxInclusive: boolean;
  displayMode: UsagePriceDisplayMode;
  taxId: TaxId | null;
  tiers: ReadonlyArray<UsageTierConfig>;
  sales: ResolvedSalesConfig;
};

/** The unlimited plans on offer, in presentation order. */
export const unlimitedPlansOf = (
  sales: Pick<ResolvedSalesConfig, 'plans'> | undefined,
): ResolvedUnlimitedPlan[] =>
  (sales?.plans ?? []).filter((plan): plan is ResolvedUnlimitedPlan => plan.kind === 'unlimited');

const usageTierOverlaySchema = z.object({
  minQty: z.number().int().positive(),
  planCode: z.string().min(1),
  pricePerUnit: z.record(z.string(), z.number()),
  highlight: z.boolean().optional(),
});

const pricingOfferSchema = z.enum(['per_order', 'subscription']);

const trialPolicySchema = z.discriminatedUnion('by', [
  z.object({ by: z.literal('none') }),
  z.object({ by: z.literal('days'), days: z.number().int().positive() }),
  z.object({ by: z.literal('orders'), orders: z.number().int().positive() }),
]);

const perOrderPlanSchema = z
  .object({
    id: z.string().min(1),
    kind: z.literal('per_order'),
    tiers: z.array(usageTierOverlaySchema).min(1).optional(),
    displayMode: z.enum(['per_unit', 'packaged']).optional(),
    taxInclusive: z.boolean().optional(),
  })
  .strict();

const unlimitedPlanFields = {
  id: z.string().min(1),
  kind: z.literal('unlimited'),
  name: z
    .object({
      en: z.string().min(1).optional(),
      ar: z.string().min(1).optional(),
      ur: z.string().min(1).optional(),
    })
    .optional(),
  planCode: z.string().min(1).optional(),
  perVehiclePricePerMonth: z.record(z.string(), z.number().nonnegative()),
  taxInclusive: z.boolean().optional(),
  /**
   * Retired: the free allowance is one per audience (`freeCredits`) and
   * carries over to whichever plan is picked. Still accepted so a payload
   * saved before then is not rejected whole; dropped at resolve.
   */
  trial: trialPolicySchema.optional(),
  minTrucks: z.number().int().positive().optional(),
  maxTrucks: z.number().int().positive().optional(),
  highlight: z.boolean().optional(),
};

const salesPlanSchema = z.discriminatedUnion('kind', [
  perOrderPlanSchema,
  z.object(unlimitedPlanFields).strict(),
]);

const menuOptionSchema = z
  .object({
    id: z.string().min(1),
    plan: z.string().min(1),
    qty: z.number().int().positive().optional(),
    vehicleCount: z.number().int().positive().optional(),
    highlight: z.boolean().optional(),
  })
  .strict()
  .refine(option => (option.qty === undefined) !== (option.vehicleCount === undefined), {
    message: 'set exactly one of qty (per-order plan) or vehicleCount (unlimited plan)',
  });

/**
 * The audience's one free allowance, spent before any plan bills: a
 * count of orders, or a number of days. No `by` is a count (the shape
 * every payload had before days existed).
 */
const freeCreditsOverlaySchema = z.union([
  z
    .object({
      by: z.literal('days'),
      days: z.number().int().positive(),
      startsOn: z.enum(['first_waybill', 'signup']).default('first_waybill'),
    })
    .strict(),
  z.object({
    by: z.literal('orders').optional(),
    qty: z.number().int().min(0),
    // Same single source of truth as everywhere else, but read lazily:
    // `@hero/schema` imports `@hero/core`, so touching this value at
    // module-eval time here would hit its temporal dead zone during the
    // circular initialization. `z.lazy` defers the read to parse time,
    // by when both packages are fully initialized.
    mode: z.lazy(() => subscriptionGrantModeSchema),
    expiresInDays: z.number().int().positive().optional(),
  }),
]);

// Strict so a payload still in the pre-plans shape (`tiers`, `offers`,
// `subscription` at the audience level) is rejected and reported, not
// half-applied.
const audienceOverlaySchema = z
  .object({
    taxId: z.enum(['saudi_vat']).nullable().optional(),
    /**
     * Signup free-credit policy for this audience — the per-grant
     * successor to the fixed free allowance. Lives on the pricing flag so
     * a single per-business flag tunes both prices and the giveaway.
     */
    freeCredits: freeCreditsOverlaySchema.optional(),
    flow: z.enum(['choose_model', 'menu']).optional(),
    /** Replaces the catalog plans as a whole, in presentation order. */
    plans: z.array(salesPlanSchema).min(1).optional(),
    menu: z.array(menuOptionSchema).min(1).optional(),
  })
  .strict()
  .superRefine((slice, ctx) => {
    const plans = slice.plans ?? [];
    const ids = new Set<string>();
    plans.forEach((plan, index) => {
      if (ids.has(plan.id)) {
        ctx.addIssue({ code: 'custom', path: ['plans', index, 'id'], message: 'duplicate plan id' });
      }
      ids.add(plan.id);
      if (
        plan.kind === 'unlimited' &&
        plan.minTrucks !== undefined &&
        plan.maxTrucks !== undefined &&
        plan.minTrucks > plan.maxTrucks
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['plans', index, 'maxTrucks'],
          message: 'maxTrucks is below minTrucks',
        });
      }
    });
    if (plans.filter(plan => plan.kind === 'per_order').length > 1) {
      ctx.addIssue({ code: 'custom', path: ['plans'], message: 'only one per_order plan allowed' });
    }
    if (!slice.plans) return;
    const optionIds = new Set<string>();
    (slice.menu ?? []).forEach((option, index) => {
      if (optionIds.has(option.id)) {
        ctx.addIssue({ code: 'custom', path: ['menu', index, 'id'], message: 'duplicate option id' });
      }
      optionIds.add(option.id);
      const plan = plans.find(entry => entry.id === option.plan);
      if (!plan) {
        ctx.addIssue({ code: 'custom', path: ['menu', index, 'plan'], message: 'no plan with this id' });
      } else if (plan.kind === 'per_order' && option.qty === undefined) {
        ctx.addIssue({ code: 'custom', path: ['menu', index, 'qty'], message: 'per_order option needs qty' });
      } else if (plan.kind === 'unlimited') {
        if (option.vehicleCount === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['menu', index, 'vehicleCount'],
            message: 'unlimited option needs vehicleCount',
          });
        } else if (truckCountIssue(plan, option.vehicleCount)) {
          ctx.addIssue({
            code: 'custom',
            path: ['menu', index, 'vehicleCount'],
            message: 'outside the plan truck range',
          });
        }
      }
    });
  });

const usagePricingPayloadSchema = z
  .object({
    org: audienceOverlaySchema.optional(),
    freelancer: audienceOverlaySchema.optional(),
  })
  .passthrough();

/** The `wathiqa_plus_pricing` PostHog payload, as the resolver reads it. */
export type UsagePricingPayload = z.infer<typeof usagePricingPayloadSchema>;

/**
 * Validate a `wathiqa_plus_pricing` payload against the same schema the
 * resolver uses — an invalid one is ignored in production, so editors
 * check it before it is pasted into PostHog.
 */
export function validateUsagePricingPayload(
  raw: unknown,
): { ok: true; payload: UsagePricingPayload } | { ok: false; issues: string[] } {
  const parsed = usagePricingPayloadSchema.safeParse(raw);
  if (parsed.success) return { ok: true, payload: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map(
      issue => `${issue.path.length ? issue.path.join('.') : '(root)'}: ${issue.message}`,
    ),
  };
}

const toUsageTiers = (raw: ReadonlyArray<z.infer<typeof usageTierOverlaySchema>>): UsageTierConfig[] =>
  raw.map(tier => ({
    minQty: tier.minQty,
    planCode: tier.planCode,
    pricePerUnit: tier.pricePerUnit,
    ...(tier.highlight ? { highlight: true } : {}),
  }));

/**
 * Catalog defaults, optionally overlaid by a PostHog payload.
 *
 * An audience slice's `plans` replaces the catalog plans as a whole; a
 * per-order plan that leaves out its ladder keeps the catalog one.
 * Invalid JSON is ignored (catalog wins) — a bad paste in PostHog must
 * not break checkout. Overlay is applied only for `wathiqa_plus`; Hero
 * always uses the catalog so a WhatsApp experiment cannot reprice the
 * dashboard.
 */
export function resolveUsagePricingConfig(input: {
  business: Business;
  audience?: SubscriptionAudience;
  currency: Currency;
  overlay?: unknown;
  variant?: string;
  /** Fired when overlay JSON is present but invalid — catalog still wins. */
  onInvalidOverlay?: (error: z.ZodError) => void;
}): ResolvedUsagePricing {
  const audience = input.audience ?? DEFAULT_SUBSCRIPTION_AUDIENCE;
  const catalogSales = salesConfig(input.business, audience);
  const taxFromCurrency = SUBSCRIPTION_CONFIG.taxByCurrency[input.currency] ?? null;
  const build = (sales: SalesConfig, taxId: TaxId | null) =>
    buildPricing({ business: input.business, audience, sales, taxId, variant: input.variant });

  const base = build(catalogSales, taxFromCurrency?.id ?? null);
  if (input.business !== 'wathiqa_plus' || input.overlay == null) {
    return base;
  }

  const parsed = usagePricingPayloadSchema.safeParse(input.overlay);
  if (!parsed.success) {
    input.onInvalidOverlay?.(parsed.error);
    return base;
  }

  const slice = parsed.data[audience];
  if (!slice) return base;

  return build(
    {
      flow: slice.flow ?? catalogSales.flow,
      plans: slice.plans ?? catalogSales.plans,
      ...(slice.menu || catalogSales.menu ? { menu: slice.menu ?? catalogSales.menu } : {}),
    },
    slice.taxId === undefined ? base.taxId : slice.taxId,
  );
}

const buildPricing = (input: {
  business: Business;
  audience: SubscriptionAudience;
  sales: SalesConfig;
  taxId: TaxId | null;
  variant?: string;
}): ResolvedUsagePricing => {
  const perOrder = input.sales.plans.find(
    (plan): plan is PerOrderPlanConfig => plan.kind === 'per_order',
  );
  const tiers = perOrder?.tiers
    ? toUsageTiers(perOrder.tiers)
    : usageOrderTiers(input.business, input.audience);
  return {
    ...(input.variant ? { variant: input.variant } : {}),
    taxInclusive:
      perOrder?.taxInclusive ?? usageTaxConfig(input.business, input.audience).taxInclusive,
    displayMode: perOrder?.displayMode ?? usagePriceDisplayMode(input.business, input.audience),
    taxId: input.taxId,
    tiers,
    sales: normalizeSales(input.business, input.sales, tiers),
  };
};

/**
 * Keep only what the business can actually sell: a per-order plan needs
 * a ladder, an unlimited plan a seat plan with `unlimitedOrders` on this
 * business, `seats` some other seat plan. A `menu` with no sellable
 * option left becomes `choose_model`, so a bad experiment never leaves
 * the buyer with nothing to pick.
 */
const normalizeSales = (
  business: Business,
  raw: SalesConfig,
  tiers: ReadonlyArray<UsageTierConfig>,
): ResolvedSalesConfig => {
  const businessPlans = getPlansForBusiness(business);
  const fallbackPlanCode = defaultUnlimitedPlanCode(business);
  const sellsSeats = businessPlans.some(code => !isUsagePlan(code) && !planHasUnlimitedOrders(code));

  const plans: ResolvedSalesPlan[] = [];
  for (const plan of raw.plans) {
    if (plans.some(kept => kept.id === plan.id)) continue;
    if (plan.kind === 'per_order') {
      if (tiers.length > 0 && !plans.some(kept => kept.kind === 'per_order')) {
        plans.push({ id: plan.id, kind: 'per_order' });
      }
    } else if (plan.kind === 'seats') {
      if (sellsSeats) plans.push({ id: plan.id, kind: 'seats' });
    } else {
      const planCode = plan.planCode ?? fallbackPlanCode;
      if (planCode && businessPlans.includes(planCode) && planHasUnlimitedOrders(planCode)) {
        const kept: typeof plan & { trial?: unknown } = { ...plan };
        delete kept.trial;
        plans.push({ ...kept, planCode });
      }
    }
  }

  const menu = (raw.menu ?? []).flatMap((option): ResolvedMenuOption[] => {
    const plan = plans.find(entry => entry.id === option.plan);
    const highlight = option.highlight ? { highlight: true } : {};
    if (plan?.kind === 'per_order' && option.qty !== undefined) {
      return [{ id: option.id, kind: 'per_order', plan: plan.id, qty: option.qty, ...highlight }];
    }
    if (
      plan?.kind === 'unlimited' &&
      option.vehicleCount !== undefined &&
      !truckCountIssue(plan, option.vehicleCount)
    ) {
      return [
        {
          id: option.id,
          kind: 'unlimited',
          plan: plan.id,
          vehicleCount: option.vehicleCount,
          ...highlight,
        },
      ];
    }
    return [];
  });

  const offers = [
    ...new Set(
      plans.map((plan): PricingOffer => (plan.kind === 'per_order' ? 'per_order' : 'subscription')),
    ),
  ];
  return {
    flow: raw.flow === 'menu' && menu.length > 0 ? 'menu' : 'choose_model',
    offers,
    plans,
    menu,
  };
};

/** A resolved signup free-credit policy — catalog default, optionally overlaid. */
export type ResolvedFreeCreditsPolicy =
  | {
      by: 'orders';
      qty: number;
      mode: SubscriptionGrantMode;
      expiresInDays?: number;
    }
  | { by: 'days'; days: number; startsOn: FreeWindowStart };

/**
 * Catalog free-credit policy, optionally overlaid by the same PostHog
 * pricing payload that carries the tiers.
 *
 * Reads `payload[audience].freeCredits`. Overlay is applied only for
 * `wathiqa_plus` today (same guard as {@link resolveUsagePricingConfig}
 * so a WhatsApp experiment cannot change the Hero dashboard); when a
 * `hero_pricing` flag lands, add `'hero'` to the guard. Invalid JSON is
 * ignored — catalog wins — so a bad paste in PostHog never breaks
 * signup.
 */
export function resolveFreeCreditsPolicy(input: {
  business: Business;
  audience?: SubscriptionAudience;
  overlay?: unknown;
  /** Fired when overlay JSON is present but invalid — catalog still wins. */
  onInvalidOverlay?: (error: z.ZodError) => void;
}): ResolvedFreeCreditsPolicy {
  const audience = input.audience ?? DEFAULT_SUBSCRIPTION_AUDIENCE;
  const base: ResolvedFreeCreditsPolicy = toResolvedFreeCredits(
    freeCreditsPolicy(input.business, audience),
  );

  if (input.business !== 'wathiqa_plus' || input.overlay == null) {
    return base;
  }

  const parsed = usagePricingPayloadSchema.safeParse(input.overlay);
  if (!parsed.success) {
    input.onInvalidOverlay?.(parsed.error);
    return base;
  }

  const slice = parsed.data[audience]?.freeCredits;
  if (!slice) return base;
  return toResolvedFreeCredits(slice);
}

const toResolvedFreeCredits = (policy: FreeCreditsPolicy): ResolvedFreeCreditsPolicy =>
  policy.by === 'days'
    ? { by: 'days', days: policy.days, startsOn: policy.startsOn }
    : {
        by: 'orders',
        qty: policy.qty,
        mode: policy.mode,
        ...(policy.expiresInDays ? { expiresInDays: policy.expiresInDays } : {}),
      };

/** Funnel / Billing snapshot — same shape, JSON-safe. */
export type UsagePricingSnapshot = {
  variant?: string;
  taxInclusive: boolean;
  displayMode: UsagePriceDisplayMode;
  taxId: TaxId | null;
  tiers: UsageTierConfig[];
  /** Absent on snapshots written before the plans config existed. */
  sales?: ResolvedSalesConfig;
};

export const toUsagePricingSnapshot = (resolved: ResolvedUsagePricing): UsagePricingSnapshot => ({
  ...(resolved.variant ? { variant: resolved.variant } : {}),
  taxInclusive: resolved.taxInclusive,
  displayMode: resolved.displayMode,
  taxId: resolved.taxId,
  tiers: [...resolved.tiers],
  sales: {
    flow: resolved.sales.flow,
    offers: [...resolved.sales.offers],
    plans: [...resolved.sales.plans],
    menu: [...resolved.sales.menu],
  },
});

const salesSnapshotSchema = z.object({
  flow: z.enum(['choose_model', 'menu']),
  offers: z.array(pricingOfferSchema),
  plans: z.array(
    z.discriminatedUnion('kind', [
      z.object({ id: z.string().min(1), kind: z.literal('per_order') }),
      z.object({ ...unlimitedPlanFields, planCode: z.string().min(1) }),
      z.object({ id: z.string().min(1), kind: z.literal('seats') }),
    ]),
  ),
  menu: z.array(
    z.discriminatedUnion('kind', [
      z.object({
        id: z.string().min(1),
        kind: z.literal('per_order'),
        plan: z.string().min(1),
        qty: z.number().int().positive(),
        highlight: z.boolean().optional(),
      }),
      z.object({
        id: z.string().min(1),
        kind: z.literal('unlimited'),
        plan: z.string().min(1),
        vehicleCount: z.number().int().positive(),
        highlight: z.boolean().optional(),
      }),
    ]),
  ),
});

const usagePricingSnapshotSchema = z.object({
  variant: z.string().optional(),
  taxInclusive: z.boolean(),
  displayMode: z.enum(['per_unit', 'packaged']).optional(),
  taxId: z.enum(['saudi_vat']).nullable(),
  tiers: z.array(usageTierOverlaySchema).min(1),
  // A snapshot from before the plans config still prices off its ladder.
  sales: salesSnapshotSchema.optional().catch(undefined),
});

/** Sticky Lead / confirmation payload — invalid JSON is ignored. */
export function parseUsagePricingSnapshot(raw: unknown): UsagePricingSnapshot | undefined {
  const parsed = usagePricingSnapshotSchema.safeParse(raw);
  if (!parsed.success) return undefined;
  return {
    ...(parsed.data.variant ? { variant: parsed.data.variant } : {}),
    taxInclusive: parsed.data.taxInclusive,
    displayMode: parsed.data.displayMode ?? 'per_unit',
    taxId: parsed.data.taxId,
    tiers: toUsageTiers(parsed.data.tiers),
    ...(parsed.data.sales ? { sales: parsed.data.sales } : {}),
  };
}
